import React, { useState } from 'react';
import { Menu, Search, LogOut, Settings, ChevronDown, Command, Sparkles } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import NotificationBell from '../notifications/NotificationBell';
import { useNavigate } from 'react-router-dom';

const Topbar = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/companies?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200/80 flex items-center justify-between px-4 md:px-8 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.03)] transition-all">
      {/* Left: Mobile Menu & Search */}
      <div className="flex items-center space-x-4 flex-1 max-w-lg">
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100/80 md:hidden transition"
        >
          <Menu className="w-5 h-5" />
        </button>

        <form onSubmit={handleSearchSubmit} className="relative w-full hidden sm:block">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search companies, admins, or ID prefixes..."
            className="w-full pl-10 pr-12 py-2 rounded-xl bg-slate-50/90 border border-slate-200/90 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
          />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-slate-200/60 border border-slate-300/40 text-[10px] font-mono text-slate-500 pointer-events-none">
            <Command className="w-2.5 h-2.5" />
            <span>K</span>
          </div>
        </form>
      </div>

      {/* Right Actions: Notifications & Profile */}
      <div className="flex items-center space-x-3.5">
        {/* Environment Tag */}
        <div className="hidden lg:flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/60 text-[11px] font-bold text-emerald-700">
          <Sparkles className="w-3 h-3 text-emerald-600 animate-pulse" />
          <span>Live Workspace</span>
        </div>

        <div className="h-5 w-[1px] bg-slate-200 hidden sm:block" />

        <NotificationBell />

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex items-center space-x-2.5 p-1.5 rounded-2xl hover:bg-slate-100/80 border border-transparent hover:border-slate-200/60 transition"
          >
            <div className="relative">
              <img
                src={user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80'}
                alt={user?.name}
                className="w-8 h-8 rounded-xl object-cover shadow-sm ring-2 ring-emerald-500/20"
              />
              <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
            </div>
            <div className="hidden sm:flex flex-col text-left">
              <span className="text-xs font-extrabold text-slate-800 font-display leading-tight">
                {user?.name}
              </span>
              <span className="text-[10px] font-semibold text-slate-400 leading-tight">
                Master Admin
              </span>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 hidden sm:inline-block transition-transform duration-200 ${profileOpen ? 'rotate-180 text-emerald-600' : ''}`} />
          </button>

          {profileOpen && (
            <div
              onMouseLeave={() => setProfileOpen(false)}
              className="absolute right-0 mt-2 w-60 rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/80 shadow-[0_12px_40px_-5px_rgba(0,0,0,0.1)] p-2 z-50 animate-fadeIn"
            >
              <div className="px-3.5 py-3 border-b border-slate-100 mb-1.5 bg-gradient-to-r from-emerald-50/50 to-transparent rounded-xl">
                <p className="text-xs font-extrabold text-slate-900 font-display">{user?.name}</p>
                <p className="text-[11px] text-slate-500 font-medium truncate mt-0.5">{user?.email}</p>
                <span className="inline-block mt-2 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[9px] font-extrabold uppercase tracking-wider">
                  Super Administrator
                </span>
              </div>

              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/settings');
                }}
                className="w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/80 transition"
              >
                <Settings className="w-4 h-4 text-emerald-600" />
                <span>Account Settings</span>
              </button>

              <button
                onClick={logout}
                className="w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-rose-600 hover:bg-rose-50 transition mt-0.5"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;

